import { useEffect, useRef, useState } from "react";
import { ArrowDownRight, ArrowUp, Menu, Search, X } from "lucide-react";
import { Lotus } from "./TraditionArt";
import { site } from "../content/site";

const sectionLinks = [
  ["Origin", "origin"],
  ["Programmes", "initiatives"],
  ["Temple", "ashram"],
] as const;

export function SiteHeader({ currentPath = "/" }: { currentPath?: string }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const homePrefix = currentPath === "/" ? "" : "/";

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && menuOpen) {
        setMenuOpen(false);
        menuButton.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <>
      <div className="sacred-thread" aria-hidden="true">
        <span>वेद</span><i /><span>धर्म</span><i /><span>सेवा</span>
      </div>
      <header className="site-header">
        <div className="container header-inner">
          <a className="brand" href="/" aria-label="Sri Devinatha Mission home">
            <img src="/brand/emblem.jpg" width="42" height="53" alt="" />
            <span><strong>Sri Devinatha</strong><small>Mission</small></span>
          </a>
          <a className="header-search" href="/articles/#article-search-input" aria-label="Search articles"><Search size={19} aria-hidden="true" /></a>
          <button
            ref={menuButton}
            className="menu-button"
            type="button"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-controls="main-navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X /> : <Menu />}
          </button>
          <nav id="main-navigation" className={menuOpen ? "main-nav is-open" : "main-nav"} aria-label="Main navigation">
            {sectionLinks.map(([label, id]) => (
              <a key={id} href={`${homePrefix}#${id}`} onClick={() => setMenuOpen(false)}>{label}</a>
            ))}
            <a href="/articles/" aria-current={currentPath.startsWith("/articles") ? "page" : undefined} onClick={() => setMenuOpen(false)}>Articles</a>
            <a href="/events/" aria-current={currentPath.startsWith("/events") ? "page" : undefined} onClick={() => setMenuOpen(false)}>Events</a>
            <a href="/gallery/" aria-current={currentPath.startsWith("/gallery") ? "page" : undefined} onClick={() => setMenuOpen(false)}>Gallery</a>
            <a className="nav-contact" href={`${homePrefix}#contact`} onClick={() => setMenuOpen(false)}>
              Contact <ArrowDownRight size={16} aria-hidden="true" />
            </a>
          </nav>
        </div>
      </header>
    </>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="container footer-inner">
        <div className="footer-brand">
          <img src="/brand/emblem.jpg" width="34" height="43" alt="" />
          <span>Sri Devinatha Mission</span>
        </div>
        <p>A living Vedic tradition.<br />Spiritual practice, teaching & service.</p>
        <div className="footer-links">
          <a href="/articles/">Articles</a>
          <a href="/events/">Events</a>
          <a href="/gallery/">Gallery</a>
          <a href={site.articleArchive} target="_blank" rel="noreferrer">Substack archive</a>
          <a href="/the-bharatheeya-institute">The Bharatheeya Institute</a>
          <a href="#top">Back to top <ArrowUp size={14} aria-hidden="true" /></a>
        </div>
      </div>
      <div className="container footer-bottom">
        <span>© 2026 Sri Devinatha Mission. All rights reserved.</span>
        <Lotus />
        <div className="footer-credit">
          <span>Powered by</span>
          <a href={site.poweredByUrl}>
            <img src="/brand/aieun-labs-logo.webp" width="42" height="30" alt="" loading="lazy" decoding="async" />
            <strong>AIEUN Labs</strong>
          </a>
        </div>
      </div>
    </footer>
  );
}
